import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '../Button/Button'
import { useIsDark } from './useIsDark'
import { useTargetRects, type Rect } from '../CoachMark/useTargetRects'

/**
 * First-run workbench tour: an informative walkthrough that dims the whole
 * screen, rings the feature being described and explains it in a coach card
 * with Step X/N + Skip/Next controls. Nothing underneath is clickable — the
 * user reads and advances (or skips); they don't perform the actions. The
 * step sequence lives in Workbench.tsx; this file owns the visual + the
 * done-flag storage.
 */

const KEY = 'algobuilder.workbenchOnboardingDone'

export const onboardingStorage = {
  isDone(): boolean {
    try {
      return localStorage.getItem(KEY) === '1'
    } catch {
      return false
    }
  },
  markDone(): void {
    try {
      localStorage.setItem(KEY, '1')
    } catch {
      // storage unavailable — the tour will show again next time, harmless
    }
  },
  reset(): void {
    try {
      localStorage.removeItem(KEY)
    } catch {
      // ignore
    }
  },
}

const PADDING = 6
const HOLE_RADIUS = 8
const RING_PADDING = 1
const RING_RADIUS = 6
const TOOLTIP_GAP = 12
const TOOLTIP_WIDTH = 280
// Rough card height for viewport clamping (copy + footer); exactness doesn't
// matter, it only keeps the card from hugging the screen edge.
const EST_CARD_H = 140

export type OnboardingPlacement = 'bottom' | 'left' | 'top' | 'right'

export interface OnboardingTourProps {
  /** Every matched target gets its own highlight ring; the dim hole and the card anchor to their union. */
  targetSelectors: string[]
  copy: string
  placement: OnboardingPlacement
  /** 1-based, display only ("Step 2/5"). */
  step: number
  totalSteps: number
  onNext: () => void
  onSkip: () => void
}

export function OnboardingTour({ targetSelectors, copy, placement, step, totalSteps, onNext, onSkip }: OnboardingTourProps) {
  const rects = useTargetRects(targetSelectors)
  const isDark = useIsDark()
  const nextRef = useRef<HTMLButtonElement>(null)

  // Keep focus on Next: pointer-events can't stop keystrokes, and the chat
  // composer autofocuses — without this, typing (and Enter) would land in the
  // app underneath the scrim. Enter advancing the tour is the bonus.
  useEffect(() => {
    nextRef.current?.focus()
  }, [step])

  if (!rects) return null // first measure hasn't run yet (never paints — layout effect)

  // Prototype scrim; slightly gentler in dark mode where the chart is already
  // near-black and would otherwise wash out completely.
  const dim = isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.6)'
  const hasTargets = rects.length > 0

  let tipStyle: React.CSSProperties
  let arrowStyle: React.CSSProperties = {}
  let hole: Rect | null = null

  if (hasTargets) {
    const right = Math.max(...rects.map((r) => r.left + r.width))
    const bottom = Math.max(...rects.map((r) => r.top + r.height))
    const union = {
      top: Math.min(...rects.map((r) => r.top)),
      left: Math.min(...rects.map((r) => r.left)),
    }
    hole = {
      top: union.top - PADDING,
      left: union.left - PADDING,
      width: right - union.left + PADDING * 2,
      height: bottom - union.top + PADDING * 2,
    }

    // Side placements center on the hole; clamp so the card can't slide off
    // the bottom (the composer target sits at the viewport edge).
    const sideTop = Math.min(
      Math.max(8, hole.top + hole.height / 2 - 56),
      window.innerHeight - EST_CARD_H - 8,
    )
    if (placement === 'bottom') {
      tipStyle = {
        top: hole.top + hole.height + TOOLTIP_GAP,
        left: Math.max(8, Math.min(hole.left + hole.width / 2 - TOOLTIP_WIDTH / 2, window.innerWidth - TOOLTIP_WIDTH - 8)),
        width: TOOLTIP_WIDTH,
      }
      arrowStyle = {
        top: -6,
        left: hole.left + hole.width / 2 - (tipStyle.left as number) - 6,
        borderLeft: '1px solid var(--border)',
        borderTop: '1px solid var(--border)',
      }
    } else if (placement === 'top') {
      tipStyle = {
        top: hole.top - TOOLTIP_GAP,
        left: Math.max(8, Math.min(hole.left + hole.width / 2 - TOOLTIP_WIDTH / 2, window.innerWidth - TOOLTIP_WIDTH - 8)),
        width: TOOLTIP_WIDTH,
        transform: 'translateY(-100%)',
      }
      arrowStyle = {
        bottom: -6,
        left: hole.left + hole.width / 2 - (tipStyle.left as number) - 6,
        borderRight: '1px solid var(--border)',
        borderBottom: '1px solid var(--border)',
      }
    } else if (placement === 'right') {
      tipStyle = {
        top: sideTop,
        left: Math.max(8, Math.min(hole.left + hole.width + TOOLTIP_GAP, window.innerWidth - TOOLTIP_WIDTH - 8)),
        width: TOOLTIP_WIDTH,
      }
      arrowStyle = {
        left: -6,
        top: Math.max(10, hole.top + hole.height / 2 - sideTop - 6),
        borderLeft: '1px solid var(--border)',
        borderBottom: '1px solid var(--border)',
      }
    } else {
      // left of the target
      tipStyle = {
        top: sideTop,
        left: Math.max(8, hole.left - TOOLTIP_GAP - TOOLTIP_WIDTH),
        width: TOOLTIP_WIDTH,
      }
      arrowStyle = {
        right: -6,
        top: Math.max(10, hole.top + hole.height / 2 - sideTop - 6),
        borderRight: '1px solid var(--border)',
        borderTop: '1px solid var(--border)',
      }
    }
  } else {
    // No target mounted (e.g. chat collapsed / mobile) — full dim, centered card.
    tipStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: TOOLTIP_WIDTH }
  }

  return createPortal(
    // Portal lands on <body>, outside #root — re-scope the design tokens with
    // .v1, but kill its opaque background-color (tokens.css paints .v1 with
    // var(--background), which would black out the whole viewport here).
    // pointer-events-auto on the root is the whole "informative-only"
    // mechanism: every click, hover and wheel terminates on the overlay.
    <div
      className="v1 fixed inset-0 z-[100] pointer-events-auto"
      style={{ backgroundColor: 'transparent' }}
      role="dialog"
      aria-modal="true"
      aria-label="Workbench tour"
    >
      {hole ? (
        // Spotlight: the giant shadow spread paints the dim everywhere except
        // the (genuinely rounded) hole over the targets.
        <div
          className="absolute"
          style={{ ...hole, borderRadius: HOLE_RADIUS, boxShadow: `0 0 0 200vmax ${dim}` }}
        />
      ) : (
        <div className="absolute inset-0" style={{ background: dim }} />
      )}
      {/* One highlight ring per target (e.g. both Buy and Sell entry buttons). */}
      {rects.map((r, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            top: r.top - RING_PADDING,
            left: r.left - RING_PADDING,
            width: r.width + RING_PADDING * 2,
            height: r.height + RING_PADDING * 2,
            borderRadius: RING_RADIUS,
            boxShadow: '0 0 0 2px var(--primary)',
          }}
        />
      ))}
      {/* Coach card. */}
      <div className="absolute rounded-md border border-border bg-card shadow-popover px-4 py-3" style={tipStyle}>
        {hasTargets && <div className="absolute h-3 w-3 bg-card" style={{ ...arrowStyle, transform: 'rotate(45deg)' }} />}
        <p className="text-body text-foreground leading-relaxed" style={{ fontSize: 12 }}>
          {copy}
        </p>
        <div className="mt-2.5 flex items-center justify-between gap-2">
          <span className="text-caption text-muted-foreground tabular-nums">
            Step {step}/{totalSteps}
          </span>
          <div className="flex items-center gap-1">
            {step < totalSteps && (
              <Button type="button" variant="ghost" size="xs" onClick={onSkip} className="text-muted-foreground">
                Skip
              </Button>
            )}
            <Button ref={nextRef} type="button" size="xs" onClick={onNext}>
              {step === totalSteps ? 'Done' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
